'use client'

/**
 * Root-level error boundary for the app.
 * Catches errors in page rendering within the root layout.
 */

import { useEffect } from 'react'
import { Button, Card, Space, Alert } from 'antd'
import { ExclamationCircleOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons'
import { Typography } from '@/shared/components/typography/SafeTypography'

const { Title, Text } = Typography

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error('Root error boundary caught:', error)
  }, [error])

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    }}>
      <Card style={{ maxWidth: '500px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            margin: '0 auto 16px',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: '#fff2f0',
          }}>
            <ExclamationCircleOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
          </div>
          <Title level={4} style={{ margin: '0 0 8px' }}>Something went wrong</Title>
          <Text type="secondary">An unexpected error occurred.</Text>
        </div>

        <Alert
          type="error"
          showIcon
          title={error.message || 'Unknown error'}
          description={error.digest ? `Error ID: ${error.digest}` : undefined}
          style={{ marginBottom: '24px' }}
        />

        <Space style={{ width: '100%', justifyContent: 'center' }}>
          <Button type="primary" icon={<ReloadOutlined />} onClick={reset}>
            Try Again
          </Button>
          <Button icon={<HomeOutlined />} onClick={() => window.location.href = '/dashboard'}>
            Go to Dashboard
          </Button>
        </Space>
      </Card>
    </div>
  )
}
